import React, { useState } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { Users, Calendar, Lock, BellRing, Info, ChevronRight, ChevronLeft, X, CheckCircle2 } from 'lucide-react';
import { useStore, isDropUnlockedBySequence, hasActiveReservation as checkActiveReservation, canReserveNewDrop } from '../lib/store';
import { InstagramFollow } from '../components/InstagramFollow';
export function FeedPage({
  onNavigate
}: NavigationProps) {
  const {
    events,
    user,
    reservations,
    addReservation
  } = useStore();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const upcomingCampaigns = events;
  const hasPendingReview = reservations.some(r => r.status === 'redeemed');
  // Lifecycle gate: only one active reservation at a time. A user may reserve a
  // new drop only after the current one is redeemed AND that catch is reviewed.
  const hasReserved = checkActiveReservation(reservations);
  const canReserve = canReserveNewDrop(reservations);
  // Has the user caught a box before, and is there nothing new to reserve once
  // they've reviewed? Then we notify them instead of leaving the feed empty.
  const hasCaught = reservations.length > 0;
  const reservableDrops = events.filter(e => !reservations.some(r => r.eventId === e.id));
  const showNotifyBanner = hasCaught && !hasPendingReview && reservableDrops.length === 0;
  return <div className="min-h-screen bg-brand-ice pb-32 px-4 pt-6" data-id="element-1954" data-mp="3beo9tuvq6u1v">
      {hasPendingReview && <div className="bg-yellow-50 border-2 border-yellow-400 rounded-2xl p-5 mb-6 shadow-md animate-in slide-in-from-top-4" data-id="element-1955" data-mp="37d320ny1t5yj">
          <div className="flex items-start space-x-4" data-id="element-1956" data-mp="32ft40jwlflz3">
            <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shrink-0" data-id="element-1957" data-mp="2xff3ro18hgbm">
              <span className="text-2xl" data-id="element-1958" data-mp="2sftvgn06bvm8">⭐</span>
            </div>
            <div className="flex-1" data-id="element-1959" data-mp="2sieyvgg4ejm3">
              <h2 className="text-lg font-bold text-yellow-900 mb-1" data-id="element-1960" data-mp="2is6pufgjiwvy">
                Review your catch!
              </h2>
              <p className="text-yellow-800 text-sm mb-3" data-id="element-1961" data-mp="2nsm2f4t000cv">
                You have a catch waiting to be reviewed. Review it now to unlock
                the next drop.
              </p>
              <Button onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none shadow-sm" data-id="element-1962" data-mp="2duoekif1w5ks">
                Review Now
              </Button>
            </div>
          </div>
        </div>}

      {/* Reviewed, but nothing new to reserve yet → notify the user. */}
      {showNotifyBanner && <div className="bg-brand-blue rounded-2xl p-5 mb-6 shadow-md animate-in slide-in-from-top-4" data-id="element-1963" data-mp="2wrfxgqqokiob">
          <div className="flex items-start space-x-4" data-id="element-1964" data-mp="3kha6sybsjp3t">
            <div className="w-12 h-12 bg-brand-yellow rounded-full flex items-center justify-center shrink-0" data-id="element-1965" data-mp="3p8sa3t1ae1ry">
              <BellRing className="w-6 h-6 text-brand-navy" data-id="element-1966" data-mp="1dtb734w226fj" />
            </div>
            <div className="flex-1" data-id="element-1967" data-mp="18uq796kptorm">
              <h2 className="text-lg font-bold text-white mb-1" data-id="element-1968" data-mp="18tov0yryqw8b">
                You're all caught up!
              </h2>
              <p className="text-brand-light/90 text-sm" data-id="element-1969" data-mp="1nj36qdj0hpqv">
                No new drops are out right now. We'll notify you the moment the
                next one is ready — follow us on Instagram to stay in the loop.
              </p>
            </div>
          </div>
        </div>}

      {/* Instructions */}
      <div className="bg-white rounded-[1.75rem] p-5 shadow-[0_14px_34px_rgba(22,38,95,0.08)] border border-white mb-8 flex justify-between items-center" data-id="element-1970" data-mp="pxyrh74dtrsb">
        <div data-id="element-1971" data-mp="zts9h60vedhh">
          <h1 className="text-3xl font-display text-brand-navy tracking-tight mb-1" data-id="element-1972" data-mp="zt8v1zafi787">
            Welcome, {user?.name || 'Fisher'}!
          </h1>
          <p className="text-gray-600 text-sm" data-id="element-1973" data-mp="2b9796hkgrjga">Ready for your next catch?</p>
        </div>
        <button onClick={() => {
        setCurrentSlide(0);
        setShowHowItWorks(true);
      }} className="flex flex-col items-center justify-center bg-brand-light hover:bg-gray-200 text-brand-blue rounded-xl p-3 transition-colors" data-id="element-1974" data-mp="2bbjmghfa15n0">
          <Info className="w-6 h-6 mb-1" data-id="element-1975" data-mp="trgnylidnd7p" />
          <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1976" data-mp="yrmfm1onczps">
            How it works
          </span>
        </button>
      </div>

      {/* Follow us — stay up to date on new drops */}
      <InstagramFollow className="mb-8" data-id="element-1977" data-mp="18pirwwtbm0ph" />

      <div className="space-y-6" data-id="element-1978" data-mp="18ohhmuk4pi8t">
        <div className="flex items-center justify-between px-2" data-id="element-1979" data-mp="1dgr1ptykbo36">
          <h2 className="text-3xl font-display text-brand-navy tracking-tight" data-id="element-1980" data-mp="1dhakfcw1egi9">
            Coming Soon
          </h2>
        </div>

        {upcomingCampaigns.length === 0 && <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100" data-id="element-1981" data-mp="1mii9d4cny4zo">
            <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1982" data-mp="1rhl9qbhp7n1b">
              <span className="text-2xl" data-id="element-1983" data-mp="21m1qr86wza6p">🌊</span>
            </div>
            <h3 className="text-lg font-bold text-brand-blue mb-2" data-id="element-1984" data-mp="1wl49cxtqak69">
              No drops yet
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-1985" data-mp="1rnc6j8rjpdna">
              We're preparing the next catch. Check back soon!
            </p>
          </div>}

        {upcomingCampaigns.map(event => {
        const isReserved = reservations.some(r => r.eventId === event.id);
        const isEligible = isDropUnlockedBySequence(events, reservations, event.id);
        // A drop the user hasn't reserved is locked when: they still owe a
        // review, they already hold an active reservation elsewhere (one at a
        // time), or they haven't taken part in the previous drop yet.
        const isLocked = (hasPendingReview || hasReserved || !isEligible) && !isReserved && event.type === 'reservation';
        const lockReason = hasPendingReview ? 'review' : hasReserved ? 'redeem' : 'sequence';
        return <div key={event.id} className="bg-white rounded-[1.75rem] p-1 shadow-[0_14px_34px_rgba(22,38,95,0.08)] border border-white group hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden" data-id="element-1986" data-mp="mnxibgyljr9q">
              {isLocked && <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm z-30 flex flex-col items-center justify-center text-center p-6" data-id="element-1987" data-mp="ilj0n06nyxk1">
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mb-3 shadow-[0_0_20px_rgba(250,204,21,0.4)]" data-id="element-1988" data-mp="3q314fnbe9g9">
                    <Lock className="w-6 h-6 text-black" data-id="element-1989" data-mp="1y2sveyhlhko6" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2" data-id="element-1990" data-mp="1y0q9oqnh6dp9">
                    {lockReason === 'review' ? 'Review Required' : lockReason === 'redeem' ? 'Redeem First' : 'Drop Locked'}
                  </h3>
                  <p className="text-gray-300 text-sm mb-6" data-id="element-1991" data-mp="143pqqg9juqn">
                    {lockReason === 'review' ? 'Review your latest catch to unlock your next reservation.' : lockReason === 'redeem' ? 'You already have a reserved drop. Redeem it first — you can only hold one reservation at a time.' : 'You must take part in the previous drop to unlock this one.'}
                  </p>
                  {lockReason === 'review' && <Button onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1992" data-mp="f3lifgwojs5a">
                      Review Now
                    </Button>}
                  {lockReason === 'redeem' && <Button onClick={() => onNavigate('catches')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1993" data-mp="1v1sncn3m7zgs">
                      Go to My Catches
                    </Button>}
                </div>}

              <div className="relative h-40 bg-brand-purple rounded-[1.45rem] overflow-hidden mb-3" data-id="element-1994" data-mp="1c381b9gkzka0">
                <div className="absolute inset-0 bg-brand-purple/95 flex items-center justify-center" data-id="element-1995" data-mp="1h21dmyk4t58l">
                  <div className="text-center" data-id="element-1996" data-mp="1m1npp4o84fft">
                    <span className="text-brand-yellow text-xs font-bold uppercase tracking-widest mb-2 block" data-id="element-1997" data-mp="1lzt6buslovdu">
                      {event.theme}
                    </span>
                    <h3 className="text-3xl font-display text-white mb-1" data-id="element-1998" data-mp="2xfhs4iu4bod2">
                      {event.title}
                    </h3>
                    <div className="flex items-center justify-center text-brand-light/80 text-sm font-medium mt-2" data-id="element-1999" data-mp="32dt4p1bsscyz">
                      <Calendar className="w-4 h-4 mr-1.5" data-id="element-2000" data-mp="qu3loq0m7dax" />
                      {event.date || 'Available now'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4" data-id="element-2001" data-mp="c3e8xj7i9q4y">
                <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-2002" data-mp="c2cyngybd7oa">
                  {event.brandLogo && <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center p-1 shrink-0" data-id="element-2003" data-mp="75mgmo2y8208">
                      <img src={event.brandLogo} alt={event.brand} className="max-w-full max-h-full object-contain mix-blend-multiply" data-id="element-2004" data-mp="3u8r8b3pjuvgv" />
                    </div>}
                  <div className="text-center" data-id="element-2005" data-mp="1sfjrtqy7a7te">
                    <p className="text-gray-500 text-sm italic" data-id="element-2006" data-mp="1ni9ttmwqwnty">
                      "Something fishy is coming..."
                    </p>
                    {(event.instagramUrl || event.websiteUrl) && <div className="flex justify-center space-x-2 mt-2" data-id="element-2007" data-mp="3t5k83dples2e">
                        {event.instagramUrl && <a href={event.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-pink-600 bg-pink-50 px-2 py-1 rounded-md hover:bg-pink-100 transition-colors" data-id="element-2008" data-mp="azxq4qou2hut">
                            Instagram
                          </a>}
                        {event.websiteUrl && <a href={event.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md hover:bg-blue-100 transition-colors" data-id="element-2009" data-mp="3jf945r5wsivi">
                            Website
                          </a>}
                      </div>}
                  </div>
                </div>

                <div className="flex space-x-2" data-id="element-2010" data-mp="2cmcng5jvd5za">
                  <Button variant="secondary" onClick={() => onNavigate('friends')} className="flex-1 bg-brand-lavender text-brand-purple border-brand-lavender hover:bg-brand-lavender/70 text-sm py-3" data-id="element-2011" data-mp="2cn4ldn0xvfsq">
                    <Users className="w-4 h-4 mr-2" data-id="element-2012" data-mp="2qej21n7dj4wm" />
                    Invite
                  </Button>

                  <Button onClick={() => {
                if (isReserved) return;
                if (!canReserve || !isEligible) return;
                addReservation(event.id);
              }} disabled={isReserved || !canReserve || !isEligible} className={`flex-1 text-sm py-3 ${isReserved ? 'bg-green-100 text-green-700 hover:bg-green-200' : ''}`} data-id="element-2013" data-mp="2vbt3k181f70t">
                    {isReserved ? 'Reserved' : 'Reserve'}
                  </Button>
                </div>
              </div>
            </div>;
      })}
      </div>

      {/* How It Works Modal */}
      {showHowItWorks && <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-2014" data-mp="32wlox8v62ulu">
          <div className="bg-white rounded-3xl w-full max-w-sm relative overflow-hidden flex flex-col" data-id="element-2015" data-mp="2yt71vy8t9dyw">
            <button onClick={() => setShowHowItWorks(false)} className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10" data-id="element-2016" data-mp="2tvxbogdlkya0">
              <X className="w-5 h-5 text-gray-600" data-id="element-2017" data-mp="s27t3qcwn26p" />
            </button>

            <div className="p-8 text-center min-h-[320px] flex flex-col justify-center" data-id="element-2018" data-mp="n6io37i48t30">
              {currentSlide === 0 && <div className="animate-in slide-in-from-right" data-id="element-2019" data-mp="umw2nla2mj7u">
                  <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2020" data-mp="zmid5myillcy">
                    <span className="text-4xl" data-id="element-2021" data-mp="14gmnmg6zobis">📦</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2022" data-mp="19d3cjcm0itwx">
                    1. Catch
                  </h2>
                  <p className="text-gray-500 leading-relaxed" data-id="element-2023" data-mp="bryujyg00huo">
                    Reserve your spot for an upcoming drop. Show up at the
                    location, enter the code, and get your box of real products!
                  </p>
                </div>}
              {currentSlide === 1 && <div className="animate-in slide-in-from-right" data-id="element-2024" data-mp="1x37bv7s647i4">
                  <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2025" data-mp="1x2xolryiceg4">
                    <span className="text-4xl" data-id="element-2026" data-mp="2244wdr0nhdms">⭐</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2027" data-mp="1znx032twmyfv">
                    2. Review
                  </h2>
                  <p className="text-gray-500 leading-relaxed" data-id="element-2028" data-mp="29lt9nq771lbv">
                    Try the products and leave an honest review in the app. Your
                    feedback goes directly to the brands.
                  </p>
                </div>}
              {currentSlide === 2 && <div className="animate-in slide-in-from-right" data-id="element-2029" data-mp="2ekw3eehq454j">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2030" data-mp="2eknropxc75ux">
                    <span className="text-4xl" data-id="element-2031" data-mp="2se5b8aq0wiky">🔓</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2032" data-mp="2ndzptcacjsbb">
                    3. Unlock & Repeat
                  </h2>
                  <p className="text-gray-500 leading-relaxed" data-id="element-2033" data-mp="2xbdrg5rm2cjk">
                    Completing your review unlocks one new reservation. Reserve,
                    add it to your calendar, redeem on the day — then review and
                    reserve again. One drop at a time!
                  </p>
                </div>}
              {currentSlide === 3 && <div className="animate-in slide-in-from-right" data-id="element-2034" data-mp="3714326sslf07">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2035" data-mp="3bwt0a3hcajtc">
                    <span className="text-4xl" data-id="element-2036" data-mp="2x7wxoly1xz04">📈</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2037" data-mp="320q1n7lp53b5">
                    4. Level Up
                  </h2>
                  <p className="text-gray-500 leading-relaxed" data-id="element-2038" data-mp="2o6fa6iibev1e">
                    Earn points for reserving, redeeming, reviewing, and
                    inviting friends. Reach the highest level to unlock Virtual
                    Fishing!
                  </p>
                </div>}
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex items-center justify-between" data-id="element-2039" data-mp="204ttq4nw579q">
              <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} className="p-2 text-gray-400 hover:text-brand-blue disabled:opacity-30 transition-colors" data-id="element-2040" data-mp="1vedc08d1ezhk">
                <ChevronLeft className="w-6 h-6" data-id="element-2041" data-mp="1ldt9z3hp1qya" />
              </button>

              <div className="flex space-x-2" data-id="element-2042" data-mp="2j360xkv8s72o">
                {[0, 1, 2, 3].map(i => <div key={i} className={`w-2 h-2 rounded-full transition-all ${currentSlide === i ? 'bg-brand-blue w-4' : 'bg-gray-300'}`} data-id="element-2043" data-mp="2e5wdubvbs7i0" />)}
              </div>

              {currentSlide < 3 ? <button onClick={() => setCurrentSlide(Math.min(3, currentSlide + 1))} className="p-2 text-brand-blue hover:text-brand-blue-dark transition-colors" data-id="element-2044" data-mp="xqletedzby9z">
                  <ChevronRight className="w-6 h-6" data-id="element-2045" data-mp="34fwtgxsivgh8" />
                </button> : <button onClick={() => setShowHowItWorks(false)} className="p-2 text-green-500 hover:text-green-600 transition-colors" data-id="element-2046" data-mp="39eghd3qof2c8">
                  <CheckCircle2 className="w-6 h-6" data-id="element-2047" data-mp="3j4jjpujotsn8" />
                </button>}
            </div>
          </div>
        </div>}
    </div>;
}